import { useState, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  Button,
  FormControlLabel,
  RadioGroup,
} from "@mui/material";
import TextField from "components/common/TextField";
import Checkbox from "components/common/Checkbox";
import Radio from "components/common/Radio";
import Breadcrumbs from "components/common/Breadcrumbs";
import useIsMobile from "components/hooks/useIsMobile";
import AdminFamilySelect from "components/creation/AdminFamilySelect";
import { useCreateRematchSession } from "services/advisor";
import { useRunWorkflow } from "services/workflow";
import { RunWorkflowRequest } from "protogen/workflow_service_pb";
import { CurrentUserContext } from "components/context/RequireAuth";
import { useSnackpack } from "components/context/SnackpackContextProvider";
import { useNavigate, useSearchParams } from "react-router-dom";
import FlexBox from "components/common/FlexBox";
import { isUserAdmin } from "common/userUtils";
import { DatePicker } from "@mui/x-date-pickers";

export default () => {
  const isMobile = useIsMobile();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [selectedFamily, setSelectedFamily] = useState<string | null>(null);
  const [currentTasks, setCurrentTasks] = useState("");
  const [householdDescription, setHouseholdDescription] = useState("");
  const [additionalNotes, setAdditionalNotes] = useState("");
  const [immediateRematch, setImmediateRematch] = useState(false);
  const [rematchReasonSelection, setRematchReasonSelection] = useState("");
  const [rematchReasonOtherText, setRematchReasonOtherText] = useState("");
  const [communicateWithClient, setCommunicateWithClient] = useState(false);
  const [clientCommunicationDate, setClientCommunicationDate] =
    useState<Date | null>(null);
  const { pushSnack } = useSnackpack();
  const currentUser = useContext(CurrentUserContext);
  const isAdmin = isUserAdmin(currentUser);

  useEffect(() => {
    const familyParam = searchParams.get("family");
    if (familyParam) {
      setSelectedFamily(familyParam);
    }
  }, [searchParams]);

  const { request: createRematchSession, loading } = useCreateRematchSession(
    (resp) => {
      pushSnack("Rematch session created successfully!");
      if (isAdmin) {
        navigate(`/tools/rematching/${resp.rematchSessionRef}`);
        return;
      }
      setSubmitted(true);
      setCurrentTasks("");
      setHouseholdDescription("");
      setAdditionalNotes("");
      setImmediateRematch(false);
      setRematchReasonSelection("");
      setRematchReasonOtherText("");
      setCommunicateWithClient(false);
      setClientCommunicationDate(null);
    },
  );

  const { request: runWorkflow, loading: generatingAnswers } = useRunWorkflow();

  const handleGenerateAnswers = async () => {
    if (!selectedFamily) return;

    const resp = await runWorkflow(
      new RunWorkflowRequest({
        workflowName: "generate-rematch-form-answers",
        arguments: [],
        entityType: 0,
        entityRef: selectedFamily,
        runAsync: false,
      }),
    );

    if (resp?.result) {
      const result = JSON.parse(resp.result);
      setHouseholdDescription(result.household_description || "");
      setCurrentTasks(result.current_tasks || "");
      setAdditionalNotes(result.additional_notes || "");
      pushSnack("Answers generated successfully!");
    }
  };

  const isFormValid =
    selectedFamily &&
    rematchReasonSelection &&
    (rematchReasonSelection !== "Other" || rematchReasonOtherText.trim()) &&
    householdDescription.trim() &&
    currentTasks.trim() &&
    additionalNotes.trim() &&
    (!communicateWithClient || clientCommunicationDate);

  const handleCreateSession = async () => {
    if (!selectedFamily) return;
    if (!rematchReasonSelection) {
      pushSnack({
        message: "Please select a reason for rematch",
        key: new Date().getTime(),
        alertSeverity: "error",
      });
      return;
    }
    if (rematchReasonSelection === "Other" && !rematchReasonOtherText) {
      pushSnack({
        message: "Please provide details for 'Other' reason",
        key: new Date().getTime(),
        alertSeverity: "error",
      });
      return;
    }
    if (!householdDescription.trim()) {
      pushSnack({
        message: "Please describe the family's household",
        key: new Date().getTime(),
        alertSeverity: "error",
      });
      return;
    }
    if (!currentTasks.trim()) {
      pushSnack({
        message: "Please describe their top priority tasks",
        key: new Date().getTime(),
        alertSeverity: "error",
      });
      return;
    }
    if (!additionalNotes.trim()) {
      pushSnack({
        message: "Please provide important details for rematching",
        key: new Date().getTime(),
        alertSeverity: "error",
      });
      return;
    }
    if (communicateWithClient && !clientCommunicationDate) {
      pushSnack({
        message: "Please select a client communication date",
        key: new Date().getTime(),
        alertSeverity: "error",
      });
      return;
    }

    const rematchReason =
      rematchReasonSelection === "Other"
        ? rematchReasonOtherText
        : rematchReasonSelection;

    await createRematchSession({
      familyRef: selectedFamily,
      currentTasks,
      householdDescription,
      additionalNotes,
      immediateRematch,
      rematchReason,
      communicateWithClient,
      clientCommunicationDate: clientCommunicationDate
        ? clientCommunicationDate.toISOString().split("T")[0]
        : "",
    });
  };

  return (
    <Box
      sx={{
        margin: isMobile ? "" : "auto",
        maxWidth: "1000px",
        padding: isMobile ? "20px 20px 0px 20px" : "64px 64px 0 64px",
      }}
    >
      <Box display="flex" flexDirection="row">
        <Box
          display="flex"
          flexDirection="column"
          width="100%"
          marginBottom="16px"
        >
          <Breadcrumbs
            breadcrumbs={[
              { name: "Home", link: "/" },
              { name: "Tools", link: "/tools" },
            ]}
          />
          <Box
            display="flex"
            width="100%"
            gap={isMobile ? "12px" : "24px"}
            {...(isMobile && { flexDirection: "column" })}
            {...(!isMobile && {
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            })}
          >
            <Box
              display="flex"
              flexDirection="row"
              gap="12px"
              alignContent="center"
            >
              <Typography variant="display">Family rematch request</Typography>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          gap: "16px",
          paddingBottom: "16px",
        }}
      >
        {submitted ? (
          <FlexBox direction="column" sx={{ gap: "16px" }}>
            <Typography variant="h3">Submitted!</Typography>
            <Typography variant="body">
              Your rematch request has been received. Our team will follow up
              with next steps.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => {
                setSelectedFamily(null);
                setSubmitted(false);
              }}
              sx={{ maxWidth: "260px", whiteSpace: "nowrap" }}
            >
              Submit another request
            </Button>
          </FlexBox>
        ) : (
          <>
            <AdminFamilySelect
              selectedFamily={selectedFamily}
              onChange={(familyRef) => {
                setSelectedFamily(familyRef);
              }}
              title="Select Family"
            />

            {selectedFamily && (
              <>
                <Box sx={{ marginBottom: "16px" }}>
                  <Typography variant="body">
                    Please review our{" "}
                    <a
                      href="https://advisor.findfaye.com/documents/dea89b5c-e447-4a9b-8e0c-5a1456ef335b"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "inherit", textDecoration: "underline" }}
                    >
                      rematch policy
                    </a>
                  </Typography>
                </Box>
                {isAdmin && (
                  <Box sx={{ marginBottom: "16px" }}>
                    <Button
                      variant="outlined"
                      onClick={handleGenerateAnswers}
                      disabled={generatingAnswers}
                      sx={{ maxWidth: "200px", whiteSpace: "nowrap" }}
                    >
                      {generatingAnswers ? "Generating..." : "Generate answers"}
                    </Button>
                  </Box>
                )}

                <FlexBox
                  direction="column"
                  sx={{ gap: "12px", marginTop: "0" }}
                >
                  <Typography variant="bodyHeavy">
                    Why do you need this rematch? *
                  </Typography>
                  <RadioGroup
                    value={rematchReasonSelection}
                    onChange={(e) => setRematchReasonSelection(e.target.value)}
                  >
                    <FormControlLabel
                      value="Leaving Faye"
                      control={<Radio />}
                      label="Leaving Faye"
                    />
                    <FormControlLabel
                      value="Capacity Issue"
                      control={<Radio />}
                      label="Capacity Issue"
                    />
                    <FormControlLabel
                      value="Difficult client"
                      control={<Radio />}
                      label="Difficult client"
                    />
                    <FormControlLabel
                      value="Client Reactivation"
                      control={<Radio />}
                      label="Client Reactivation"
                    />
                    <FormControlLabel
                      value="Other"
                      control={<Radio />}
                      label="Other"
                    />
                  </RadioGroup>
                  {rematchReasonSelection === "Other" && (
                    <TextField
                      variant="detachedOutlined"
                      multiline
                      rows={2}
                      value={rematchReasonOtherText}
                      onChange={(e) =>
                        setRematchReasonOtherText(e.target.value)
                      }
                      fullWidth
                      placeholder="Please elaborate..."
                      sx={{ marginTop: "8px" }}
                      required
                    />
                  )}
                </FlexBox>

                <FlexBox direction="column" sx={{ gap: "8px", marginTop: "0" }}>
                  <Typography variant="bodyHeavy">
                    Describe the family's household *
                  </Typography>
                  <TextField
                    margin="normal"
                    variant="detachedOutlined"
                    multiline
                    rows={3}
                    value={householdDescription}
                    onChange={(e) => setHouseholdDescription(e.target.value)}
                    fullWidth
                    placeholder="Family members, ages, work, etc."
                    sx={{ marginTop: "0" }}
                    required
                  />
                </FlexBox>

                <FlexBox direction="column" sx={{ gap: "8px", marginTop: "0" }}>
                  <Typography variant="bodyHeavy">
                    What are their top priority tasks? *
                  </Typography>
                  <TextField
                    variant="detachedOutlined"
                    multiline
                    rows={4}
                    value={currentTasks}
                    onChange={(e) => setCurrentTasks(e.target.value)}
                    fullWidth
                    placeholder="Describe up to 5 current or upcoming tasks"
                    sx={{ marginTop: "0" }}
                    required
                  />
                </FlexBox>

                <FlexBox direction="column" sx={{ gap: "8px", marginTop: "0" }}>
                  <Typography variant="bodyHeavy">
                    Are there any other important details for rematching? *
                  </Typography>
                  <TextField
                    variant="detachedOutlined"
                    multiline
                    rows={3}
                    value={additionalNotes}
                    onChange={(e) => setAdditionalNotes(e.target.value)}
                    fullWidth
                    placeholder="Enter additional notes..."
                    sx={{ marginTop: "0" }}
                    required
                  />
                </FlexBox>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={immediateRematch}
                      onChange={(e) => setImmediateRematch(e.target.checked)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="bodyHeavy">
                        Immediate rematch
                      </Typography>
                      <Typography variant="body">
                        Leave unchecked if you plan to finish out the current
                        billing cycle
                      </Typography>
                    </Box>
                  }
                />

                <FlexBox
                  direction="column"
                  sx={{ gap: "12px", marginTop: "16px" }}
                >
                  <Typography variant="bodyHeavy">
                    Client Communication Timeline
                  </Typography>
                  <FormControlLabel
                    control={
                      <Radio
                        checked={!communicateWithClient}
                        onChange={() => {
                          setCommunicateWithClient(false);
                          setClientCommunicationDate(null);
                        }}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body">
                          No - start on this immediately
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Radio
                        checked={communicateWithClient}
                        onChange={() => setCommunicateWithClient(true)}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body">
                          Yes - I want to communicate this change to the client
                          first
                        </Typography>
                      </Box>
                    }
                  />
                  {communicateWithClient && (
                    <Box sx={{ marginLeft: "32px", marginTop: "8px" }}>
                      <Typography variant="body" sx={{ marginBottom: "8px" }}>
                        What date will you communicate this?
                      </Typography>
                      <DatePicker
                        value={clientCommunicationDate}
                        onChange={(newDate) =>
                          setClientCommunicationDate(newDate)
                        }
                        minDate={new Date()}
                        format="MM/dd/yyyy"
                        sx={{
                          width: "300px",
                          "& .MuiInputBase-root": {
                            height: "60px",
                            padding: "0 20px 0 8px",
                          },
                          "& .MuiInputBase-input": {
                            padding: "6px 8px",
                            marginTop: "0",
                          },
                        }}
                      />
                      {clientCommunicationDate && (
                        <Typography
                          variant="body"
                          sx={{ marginTop: "12px", color: "text.secondary" }}
                        >
                          We will communicate with the client on or after{" "}
                          {clientCommunicationDate.toLocaleDateString("en-US", {
                            month: "long",
                            day: "numeric",
                            year: "numeric",
                          })}
                          .
                        </Typography>
                      )}
                    </Box>
                  )}
                </FlexBox>

                <Button
                  variant="contained"
                  onClick={handleCreateSession}
                  disabled={!isFormValid || loading}
                  sx={{ maxWidth: "200px", whiteSpace: "nowrap" }}
                >
                  Submit request
                </Button>
              </>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};
